import { useTranslation } from "react-i18next";
import Modal from "react-modal";
import { CheckmarkCircleIcon, CrossCircleIcon } from "../../components/Icons";
import { License } from "../../types/License";
import DatePicker, { registerLocale } from "react-datepicker";
import { useEffect, useState } from "react";
import ja from "date-fns/locale/ja";

type ModalProps = {
    showModal: boolean;
    selectedLicense: License | undefined;
    handleClose: () => void;
    handleCloseAndRefresh: () => void;
    updateLicense: (lisence: License, request: { [name: string]: any }) => Promise<boolean | undefined>;
    errorMessage: string;
};

type MyDataPickerProps = {
    selectedDate: Date | null;
    setDate: (date: Date | null) => void;
};

/**
 * ライセンス情報編集モーダル
 *
 * @param ModalProps
 * @returns JSX.Element
 */
const LicenseEditModal: React.FC<ModalProps> = ({
    showModal,
    selectedLicense,
    handleClose,
    handleCloseAndRefresh,
    updateLicense,
    errorMessage,
}) => {
    const { t, i18n } = useTranslation();
    const [activationDate, setActivationDate] = useState<Date | null>(null);
    const [expireDate, setExpireDate] = useState<Date | null>(null);
    const [supportStartDate, setSupportStartDate] = useState<Date | null>(null);
    const [supportExpirationDate, setSupportExpirationDate] = useState<Date | null>(null);
    const [changed, setChanged] = useState<boolean>(false); // 日付が変更されたかどうか
    const [completed, setCompleted] = useState<boolean>(false); // 更新処理が完了したかどうか
    const [isProcessing, setIsProcessing] = useState<boolean>(false);
    registerLocale("ja", ja);

    useEffect(() => {
        // モーダル初期化処理
        setChanged(false);
        setCompleted(false);
        if (selectedLicense?.activation_date) {
            setActivationDate(new Date(selectedLicense?.activation_date + "+00:00"));
        } else {
            setActivationDate(null);
        }
        if (selectedLicense?.expire_date) {
            setExpireDate(new Date(selectedLicense?.expire_date + "+00:00"));
        } else {
            setExpireDate(null);
        }
        if (selectedLicense?.support_start_date) {
            setSupportStartDate(new Date(selectedLicense?.support_start_date + "+00:00"));
        } else {
            setSupportStartDate(null);
        }
        if (selectedLicense?.support_expiration_date) {
            setSupportExpirationDate(new Date(selectedLicense?.support_expiration_date + "+00:00"));
        } else {
            setSupportExpirationDate(null);
        }
    }, [selectedLicense]);

    /**
     * 実行押下時の処理
     * @returns
     */
    const handleExecute = async () => {
        if (!selectedLicense) {
            return;
        }
        setIsProcessing(true);
        try {
            const request: { [name: string]: any } = {};
            if (activationDate) {
                request["activation_date"] = activationDate.toISOString();
            }
            if (expireDate) {
                request["expire_date"] = expireDate.toISOString();
            }
            if (supportStartDate) {
                request["support_start_date"] = supportStartDate.toISOString();
            }
            if (supportExpirationDate) {
                request["support_expiration_date"] = supportExpirationDate.toISOString();
                request["last_support_expiration_date"] = supportExpirationDate.toISOString();
            }
            const result = await updateLicense(selectedLicense, request);
            if (result) {
                setCompleted(result);
            }
        } finally {
            setIsProcessing(false);
        }
    };

    /**
     * 日時ピッカー共通コンポーネント
     * @param param0
     * @returns
     */
    const MyDatePicker: React.FC<MyDataPickerProps> = ({ selectedDate, setDate }) => {
        return (
            <DatePicker
                selected={selectedDate}
                onChange={(date: Date | null) => {
                    setDate(date);
                    setChanged(true);
                }}
                disabled={false}
                showTimeSelect
                className="rounded-lg border border-gray-300 bg-gray-50 p-2 text-sm text-gray-900"
                dateFormat={i18n.language === "ja" ? "yyyy/MM/dd HH:mm" : "dd/MM/yyyy hh:mm a"}
                locale={i18n.language === "ja" ? "ja" : undefined}
            />
        );
    };

    return (
        <Modal
            id="licenseeditmodal"
            className="fixed left-1/2 top-1/2 w-10/12 max-w-lg -translate-x-1/2 -translate-y-1/2 transform whitespace-pre-wrap break-words rounded-lg border bg-white p-4 shadow-md"
            overlayClassName="fixed inset-0 bg-black bg-opacity-50"
            isOpen={showModal}
        >
            <div className="m-0 items-center justify-center p-2 sm:m-2">
                <div className="pl-2 text-base font-bold">選択したライセンス情報を編集</div>
                <hr className="my-2 border-t-2"></hr>

                {/* エラーメッセージ */}
                {errorMessage && (
                    <div className="flex items-center justify-center pt-2">
                        <CrossCircleIcon />
                        <span className="pl-1 text-center font-bold text-red-600">
                            {errorMessage.split("\n").map((line, index) => (
                                <div key={index}>{line}</div>
                            ))}
                        </span>
                    </div>
                )}

                {/* 正常に処理完了した時の表示 */}
                {completed && (
                    <>
                        <div className="flex items-center justify-center py-4">
                            <CheckmarkCircleIcon />
                            <span className="pl-2 text-center text-xl font-bold text-green-500">
                                正常に更新完了しました。
                            </span>
                        </div>
                        {/* 終了ボタン */}
                        <div className="flex items-center justify-evenly py-4">
                            <button className="btn-secondary w-[150px]" onClick={handleCloseAndRefresh}>
                                {t("CommonLabel.close")}
                            </button>
                        </div>
                    </>
                )}

                {/* 編集画面の表示 */}
                {!completed && (
                    <>
                        <div className="flex w-full justify-center">
                            {selectedLicense && (
                                <>
                                    <div className="m-0 grid w-full grid-cols-3 items-center p-2 text-base">
                                        <div className="col-span-1 px-4">{t("CommonLabel.packageName")}</div>
                                        <div className="col-span-2 pb-1">{selectedLicense.package_name}</div>
                                        <div className="col-span-1 px-4 ">{t("CommonLabel.user")}</div>
                                        <div className="col-span-2 pb-3">{selectedLicense.email || "-"}</div>

                                        {selectedLicense.permanent_flg ? (
                                            <>
                                                {/* 恒久版の場合 */}
                                                {/* 利用開始日 */}
                                                <div className="col-span-1 px-4 py-2">
                                                    {t("CommonLabel.activationDate")}
                                                </div>
                                                <div className="col-span-2 py-2">
                                                    <MyDatePicker
                                                        selectedDate={activationDate}
                                                        setDate={setActivationDate}
                                                    />
                                                </div>

                                                {/* サポート開始日時 */}
                                                <div className="col-span-1 px-4 py-2">
                                                    {t("CommonLabel.supportStartDate")}
                                                </div>
                                                <div className="col-span-2 py-2">
                                                    <MyDatePicker
                                                        selectedDate={supportStartDate}
                                                        setDate={setSupportStartDate}
                                                    />
                                                </div>

                                                {/* サポート有効期限日時 */}
                                                <div className="col-span-1 px-4 py-2 text-sm">
                                                    {t("CommonLabel.supportExpirationDate")}
                                                </div>
                                                <div className="col-span-2 py-2">
                                                    <MyDatePicker
                                                        selectedDate={supportExpirationDate}
                                                        setDate={setSupportExpirationDate}
                                                    />
                                                </div>
                                            </>
                                        ) : (
                                            <>
                                                {/* サブスクリプションの場合 */}
                                                {/* 利用開始日 */}
                                                <div className="col-span-1 px-4 py-2">
                                                    {t("CommonLabel.activationDate")}
                                                </div>
                                                <div className="col-span-2 py-2">
                                                    <MyDatePicker
                                                        selectedDate={activationDate}
                                                        setDate={setActivationDate}
                                                    />
                                                </div>

                                                {/* 有効期限 */}
                                                <div className="col-span-1 px-4 py-2">
                                                    {t("CommonLabel.expirationDate")}
                                                </div>
                                                <div className="col-span-2 py-2">
                                                    <MyDatePicker selectedDate={expireDate} setDate={setExpireDate} />
                                                </div>
                                            </>
                                        )}
                                    </div>
                                </>
                            )}
                        </div>
                        <div className="grid grid-cols-2 items-center justify-center p-2 text-base">
                            {/* 実行ボタン */}
                            <div className="col-span-2 flex items-center justify-evenly sm:col-span-1">
                                <button
                                    className={`${
                                        changed && !isProcessing ? "btn-primary bg-lime-700" : "btn-primary-disabled"
                                    } w-[150px]`}
                                    disabled={!changed || isProcessing}
                                    onClick={handleExecute}
                                >
                                    {isProcessing ? (
                                        <div className="flex items-center justify-center">
                                            <div className="mr-1 h-4 w-4 animate-spin rounded-full border-2 border-lime-100 border-t-transparent"></div>
                                            <label className="">Processing...</label>
                                        </div>
                                    ) : (
                                        t("CommonLabel.execute")
                                    )}
                                </button>
                            </div>
                            {/* キャンセル */}
                            <div className="col-span-2 flex items-center justify-evenly sm:col-span-1">
                                <button
                                    className="btn-secondary w-[150px]"
                                    onClick={handleClose}
                                    disabled={isProcessing}
                                >
                                    {t("CommonLabel.cancel")}
                                </button>
                            </div>
                        </div>
                    </>
                )}
            </div>
        </Modal>
    );
};

export default LicenseEditModal;
